<script setup lang="ts">
import { computed, defineModel, defineProps, ref, watch, onMounted } from 'vue';
import type { CallCenter } from '@/apis/generated/dialpad-contact';
import { useI18N } from '@dialpad/i18n';
import { useCallCenterStore } from '@/stores';
import type { CallCenterStore } from '@/stores';
import SearchDropdown from '@/app/components/SearchDropdown/SearchDropdown.vue';
import type {
  Item,
  ItemGroup,
} from '@/app/components/SearchDropdown/SearchDropdown.vue';
import { v4 } from 'uuid';
import { DATA_QA } from './call-center-select.constants';
import logger from '@/logger';
import { canEditCallCenter } from '@/permissions/user';
import { useUser } from '@/use-user';
import type { PDUser } from '@/use-user';
import { ValidationError } from '@/campaign/components/CampaignEdit/campaign-edit.constants';

const log = logger.getChild('CallCenterSelect');
log.debug('Mounting');
onMounted(() => {
  log.debug('Mounted');
});

const { $t } = useI18N();

const model = defineModel<CallCenter | null>({
  default: null,
});

const modelValue = ref<Item<CallCenter> | null>(null);

type Props = {
  label?: string;
  required?: boolean;
  debounce?: number;
  disabled?: boolean;
};

withDefaults(defineProps<Props>(), {
  label: '',
  required: false,
  debounce: 300,
});

const emit =
  defineEmits<
    (e: 'update:error', type: ValidationError, value: boolean) => void
  >();

const callCenterStore = useCallCenterStore();
const searchTerm = ref('');

const filter = computed<CallCenterStore['filter']>(() => ({
  search: searchTerm.value,
}));

const groups = computed(() => {
  const user = me.value;
  if (!user) return [];

  return [
    {
      items: callCenterStore.error
        ? []
        : callCenterStore.data.map((callCenter) =>
            callCenterItemFactory(callCenter, user),
          ),
    } as ItemGroup<CallCenter>,
  ];
});

const hasCallerId = (callCenter: CallCenter) => {
  return !!callCenter.didsDetails?.length;
};

const callCenterItemFactory = (
  callCenter: CallCenter,
  user: PDUser,
): Item<CallCenter> => {
  const subtitle = !canEditCallCenter(user, callCenter)
    ? $t('CALL_CENTER_SELECT_NO_PERMISSION')
    : !hasCallerId(callCenter)
    ? $t('CALL_CENTER_SELECT_NO_NUMBER')
    : undefined;
  return {
    key: callCenter.shortKey ?? `${v4()}`,
    title: callCenter.name ?? '-',
    subtitle,
    disabled: !hasCallerId(callCenter) || !canEditCallCenter(user, callCenter),
    ref: callCenter,
  };
};

const { me } = useUser();

watch(
  filter,
  () => {
    log.debug('Updating CallCenter filter');
    callCenterStore.filter = filter.value;
  },
  { immediate: true },
);

watch(
  [model, me],
  () => {
    if (!me.value) return;
    if (modelValue.value?.ref === model.value) return;
    log.debug('Model changed', model.value);
    modelValue.value =
      model.value === null
        ? null
        : callCenterItemFactory(model.value, me.value);
    emit(
      'update:error',
      ValidationError.CALL_CENTER_ACCESS_DENIED,
      model.value ? !canEditCallCenter(me.value, model.value) : false,
    );
  },
  { immediate: true },
);

watch(modelValue, () => {
  if (modelValue.value?.ref === model.value) return;
  log.debug('ModelValue changed, updating model', modelValue.value);
  model.value = modelValue.value?.ref ?? null;
  emit('update:error', ValidationError.CALL_CENTER_ACCESS_DENIED, false);
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <SearchDropdown
      v-model="modelValue"
      :disabled="disabled"
      :label="label"
      :required="required"
      :debounce="debounce"
      :loading="callCenterStore.loading"
      :messages="$ta('CALL_CENTER_SELECT_MESSAGES')"
      :list-aria-label="$t('CALL_CENTER_SELECT_ARIA_LABEL')"
      :error-messages="$ta('CALL_CENTER_SELECT_ERROR_MESSAGES')"
      :placeholder="$t('CALL_CENTER_SELECT_PLACEHOLDER')"
      :groups="groups"
      list-class="d-hmx264 d-hmn114"
      :error="callCenterStore.error"
      @update:search="(value) => (searchTerm = value)"
      @update:error="
        (value) =>
          emit('update:error', ValidationError.MISSING_CALL_CENTER, !!value)
      "
    />
  </div>
</template>
